import { useState, ReactNode } from 'react'
import { useParams, Link as RouterLink } from 'react-router-dom'
import { CircularProgress } from '@mui/material'
import BannerImage from '../../components/banner/banner-image'
import ContentContainer from '../../areas/layout/content-container'
import TrailerModal from '../../components/trailer-modal/trailer-modal'
import { useMediaState } from '../../context/media-state-context'
import { useMovieDetails } from '../../hooks/useMovieDetails'
import { MovieNotFound, LoadError } from '../Status/status-page'
import {
  StyledPageWrapper,
  StyledLoadingWrapper,
  StyledContentWrapper,
  StyledMainContent,
  StyledSidebar,
  StyledCollectionLink,
  ProvidersRow,
  ProviderChip,
  ProviderLogo,
} from '../../components/Details/details-page-styles'
import DetailsOverview from '../../components/Details/details-overview'
import DetailsHeader from '../../components/Details/details-header'
import DetailsSidebar from '../../components/Details/details-sidebar'
import CastSection from '../../components/Details/cast-section'
import CommentsSection from '../../components/Details/comments-section'
import RecommendationsSection from '../../components/Details/recommendations-section'
import { useUserRatings } from '../../hooks/useUserRatings'
import { useRecordBrowseView } from '../../hooks/use-record-browse-view'
import { getReleaseInfo } from '../../utils/release-date'

const MovieDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const { data: movie, loading, error, status, refetch } = useMovieDetails(id)
  const [trailerOpen, setTrailerOpen] = useState(false)
  const {
    favoritesSet,
    watchlistSet,
    historySet,
    toggleFavorite,
    toggleWatchlist,
    toggleHistory,
    isAuthenticated,
  } = useMediaState()
  const { mediaRating, rateMedia } = useUserRatings(
    'Movie',
    movie?.id,
    isAuthenticated,
  )

  useRecordBrowseView(
    isAuthenticated,
    movie
      ? {
          mediaType: 'Movie',
          tmdbId: movie.id,
          title: movie.title,
          posterUrl: movie.posterUrl,
          year: movie.year,
          genres: movie.genres,
        }
      : null,
  )

  if (loading) {
    return (
      <StyledLoadingWrapper>
        <CircularProgress />
      </StyledLoadingWrapper>
    )
  }

  if (error || !movie) {
    // A 404 (or a resolved-but-empty response) means the movie doesn't exist;
    // anything else is a load failure the user can retry.
    return status && status !== 404 ? (
      <LoadError onRetry={refetch} />
    ) : (
      <MovieNotFound />
    )
  }

  const meta = movie
    ? {
        title: movie.title,
        posterPath: movie.posterUrl,
        releaseYear: movie.year ? Number(movie.year) : undefined,
        genres: movie.genres,
      }
    : undefined

  const releaseInfo = getReleaseInfo(movie.releaseDate)

  return (
    <StyledPageWrapper>
      <BannerImage imageUrl={movie.backdropUrl}>
        <ContentContainer>
          <DetailsHeader
            title={movie.title}
            tagline={movie.tagline}
            posterUrl={movie.posterUrl}
            rating={movie.rating}
            year={movie.year}
            runtimeOrStatus={movie.runtime}
            genres={movie.genres}
            hasTrailer={!!movie.trailerUrl}
            onWatchTrailer={() => setTrailerOpen(true)}
            showImdbButton={!!movie.imdbId}
            onOpenImdb={() => {
              if (movie.imdbId) {
                window.open(
                  `https://www.imdb.com/title/${movie.imdbId}`,
                  '_blank',
                )
              }
            }}
            isFavorite={favoritesSet.has(movie.id)}
            isWatchlisted={watchlistSet.has(movie.id)}
            isHistory={historySet.has(movie.id)}
            onToggleFavorite={() =>
              meta && toggleFavorite(movie.id, 'movie', meta)
            }
            onToggleWatchlist={() =>
              meta && toggleWatchlist(movie.id, 'movie', meta)
            }
            onToggleHistory={() =>
              meta && toggleHistory(movie.id, 'movie', meta)
            }
            actionsEnabled={isAuthenticated}
            disableWatchlist={historySet.has(movie.id)}
            showUserRating={isAuthenticated}
            userRatingLocked={!historySet.has(movie.id)}
            userRating={mediaRating?.ratings_Value ?? null}
            onUserRatingChange={rateMedia}
            unreleased={releaseInfo.unreleased}
            releaseDateDisplay={releaseInfo.display}
          />
        </ContentContainer>
      </BannerImage>

      <ContentContainer>
        <StyledContentWrapper>
          <StyledMainContent>
            <DetailsOverview overview={movie.overview} />
            {movie.watchProviders && movie.watchProviders.length > 0 && (
              <ProvidersRow>
                {movie.watchProviders.slice(0, 10).map((p) => (
                  <ProviderChip key={p.name}>
                    {p.logoUrl ? (
                      <ProviderLogo src={p.logoUrl} alt={p.name} />
                    ) : null}
                    {p.name}
                  </ProviderChip>
                ))}
              </ProvidersRow>
            )}
            <CastSection cast={movie.cast} />
          </StyledMainContent>

          <StyledSidebar>
            <DetailsSidebar
              title='Details'
              rows={
                [
                  { label: 'Director', value: movie.director },
                  { label: 'Writer', value: movie.writer },
                  { label: 'Release Date', value: movie.releaseDate },
                  { label: 'Budget', value: movie.budget },
                  { label: 'Revenue', value: movie.revenue },
                  movie.collection && {
                    label: 'Collection',
                    value: (
                      <StyledCollectionLink
                        component={RouterLink}
                        to={`/collection/${movie.collection?.id}`}
                      >
                        {movie.collection?.name}
                      </StyledCollectionLink>
                    ),
                  },
                ].filter(Boolean) as { label: string; value?: ReactNode }[]
              }
            />
          </StyledSidebar>
        </StyledContentWrapper>

        {/* Full width, outside the main/sidebar grid */}
        <CommentsSection
          mediaType='Movie'
          tmdbMediaId={movie.id}
          lockedReason={
            releaseInfo.unreleased
              ? 'Comments open once this movie is released.'
              : undefined
          }
        />

        <RecommendationsSection
          title={`If you like ${movie.title}, you might enjoy`}
          items={movie.recommendations.map((rec) => ({
            ...rec,
            type: 'movie',
          }))}
        />
      </ContentContainer>

      <TrailerModal
        open={trailerOpen}
        onClose={() => setTrailerOpen(false)}
        title={movie.title}
        trailerUrl={movie.trailerUrl}
      />
    </StyledPageWrapper>
  )
}

export default MovieDetailPage
